/**
 * Hungarian copy for every compliance blocker/warning code the API can
 * attach to a `HotelMedia` row (source of the codes:
 * `packages/core/src/media-compliance/evaluate.ts`, `BlockerCode` /
 * `WarningCode`).
 *
 * One shared module so the wording never drifts between the places that show
 * it: `MediaBadges` (short chip label), the picker card's disabled-reason
 * list in `MediaLibraryBrowser` (full sentence — what to fix), and the media
 * detail dialog planned for a later task, which needs the identical
 * sentences for the same codes.
 */

/** Short badge label per warning code — used as the visible chip text. */
export const WARNING_BADGE_LABEL: Record<string, string> = {
  ai_labelling_required: 'AI — jelölésköteles',
  ai_unknown: 'AI — nem ismert',
  ai_change_note_fttv: 'AI-módosítás jelölve',
  // A jogalap szerint AI generálta, az érintettség szerint AI nem nyúlt
  // hozzá. A kettő nem lehet egyszerre igaz, és a rossz mezőt elhinni
  // jelölési kötelezettséget mulaszt (EU AI Act 50. cikk). A chip nem tilt,
  // csak megmutatja, hogy valakinek el kell döntenie, melyik a helyes.
  ai_contradiction: 'AI — ellentmondó adat',
  gps_present: 'GPS',
};

/**
 * Full sentence per blocker code: why the image is not attachable, and what
 * to fix. Shown next to a disabled picker card and, later, in the detail
 * dialog's compliance section.
 */
export const BLOCKER_REASON: Record<string, string> = {
  rights_unknown: 'A jogalap nincs megadva.',
  client_unconfirmed: 'Az ügyféltől kapott jogalap nincs megerősítve.',
  stock_incomplete: 'A stock-adatok hiányosak: platform, terület vagy lejárat hiányzik.',
  license_expired: 'A stock-licenc lejárt.',
  people_unknown: 'Nem ismert, van-e felismerhető személy a képen.',
  people_no_consent: 'Felismerhető személy van a képen, hozzájárulás nélkül.',
};

/**
 * Short label per compliance status. `MediaBadges` only ever reads `unknown`
 * (triage needed) and `blocked` (hard no) — those two need different wording
 * so a colleague can tell "fixable" from "not allowed" at a glance. The
 * detail dialog (task 27) also reads `attachable`, to show the positive
 * "fixed" state once the engine recomputes it after a save.
 */
export const COMPLIANCE_STATUS_LABEL = {
  unknown: 'Ismeretlen jogállás',
  blocked: 'Nem csatolható',
  attachable: 'Csatolható',
} as const;

/**
 * Hungarian label per admin-override reason (`OverrideReasonSchema`,
 * `packages/schemas/src/hotel-media.ts`). Lives here, next to the blocker
 * sentences, because the override dialog shows both together and the two
 * wordings must not drift apart.
 */
export const OVERRIDE_REASON_LABEL = {
  rights_verified_offline: 'A jogokat a rendszeren kívül ellenőriztem',
  consent_verified_offline: 'A hozzájárulást a rendszeren kívül ellenőriztem',
  other: 'Egyéb — a megjegyzésben leírva',
} as const;

export type OverrideReason = keyof typeof OVERRIDE_REASON_LABEL;
